import React from 'react';
import { debounce } from 'lodash';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps } from 'react-window';
import messages from './Messages';
import { IOption } from 'components/Select/Select';
import { IChipItem } from 'components/SelectChips/SelectChips';
import CompanyMemberView from './CompanyMemberView';
import CompanyMembersList from './CompanyMembersList';
import CompanyMembersFilters from './CompanyMembersFilters';
import notify from 'components/Notify';
import { display } from 'components/ErrorView/ErrorDialog';
import { appDispatch, IUsers, RootState } from 'store/store';
import { changeUserPhone } from 'store/users/usersSlice';
import {
  addMemberSignature,
  addMemberSignatureFn,
  addMemberToCompany,
  addMemberToCompanyFn,
  addUserRoles,
  addUserRolesFn,
  deleteUserRoles,
  deleteUserRolesFn,
  exportMembers,
  exportMembersFn,
  removeMemberSignature,
  removeMemberSignatureFn,
  removeProjectMember,
  removeProjectMemberFn,
  updateCompanyMember,
  updateCompanyMemberFn,
  updateProjectMember,
  updateProjectMemberFn,
} from 'store/companies/companiesSlice';
import {
  ICompany,
  ICompanyMember,
  ICompanyRole,
  IProject,
  IProjectMemberView,
  IProjectStore,
  IUserRoleView,
  ProjectMemberConfig,
  TProjects,
} from 'store/companies/companiesTypes';
import { IUser } from 'store/users/usersType';
import Button from 'components/Button';
import CompanyMemberModal from './CompanyMember/CompanyMemberModal';
import i18n from '../../../i18n';
import styled from 'styled-components';

interface IOwnProps {
  users: IUsers;
  onDelete: (userId: number, memberName: string) => void;
  companyId: number;
  companyOwnerId: number;
  companyMembers: ICompany['members'];
  companyMembersMap: ICompany['members'];
  getMemberRoles: (userId: number) => IChipItem[];
  companyRoleOptions: IOption[];
  company: ICompany;
  companyRoles: { [keys: number]: ICompanyRole };
  isSupport: boolean;
  projects: TProjects;
  projectObj: { [key: number]: IProject };
  thisUserId: number;
  filterUserName?: string;
}

type RoleErrorTypes = 'extraUser' | 'missingUser';
interface IDispatchToProps {
  changePhone: (
    userId: number,
    currentPhone: string,
    newPhone: string,
    newName: string
  ) => Promise<any>;
  addRoles: (
    userRoles: IUserRoleView[],
    companyId: number
  ) => ReturnType<typeof addUserRolesFn>;
  deleteRoles: (
    userRoles: IUserRoleView[],
    companyId: number
  ) => ReturnType<typeof deleteUserRolesFn>;
  addSignature: (
    companyId: number,
    userId: number,
    file: any
  ) => ReturnType<typeof addMemberSignatureFn>;
  deleteSignature: (
    companyId: number,
    userId: number
  ) => ReturnType<typeof removeMemberSignatureFn>;
  updateMember: (
    member: ICompanyMember
  ) => ReturnType<typeof updateCompanyMemberFn>;
  addMember: (
    member: ICompanyMember
  ) => ReturnType<typeof addMemberToCompanyFn>;
  removeProjectMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof removeProjectMemberFn>;
  updateProjectMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof updateProjectMemberFn>;
  exportCompanyMembers: (
    companyId: number
  ) => ReturnType<typeof exportMembersFn>;
}

interface IComponentStates {
  loading: boolean;
  nameToSearch: string;
  rolesToSearch: IOption[];
  selectedUserId?: number;
  viewState: 'default' | 'errorUsers' | 'errorRoles';
  rolesWithUsagesInProject?: undefined | Record<number, number>;
}

type IComponentProps = IOwnProps & IDispatchToProps; // & IStateToProps;

export const TranslatedTable = styled.table`
  & th,
  & td {
    text-align: ${(props: any) =>
      props.theme.direction === 'rtl' ? 'right' : 'left'};
  }
`;

// TODO: check company members type
class CompanyMembersListWrapper extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    loading: false,
    nameToSearch: this.props.filterUserName || '',
    rolesToSearch: [],
    viewState: 'default',
    rolesWithUsagesInProject: undefined,
  };

  handleDelete = (userId: number, userName: string) => () => {
    const result = window.confirm(messages.deleteUserFromCompany(userName));
    if (result) {
      this.props.onDelete(userId, userName);
    }
  };

  handleRestore = (member: ICompanyMember) => () => {
    this.props.addMember({ ...member, inactive: false });
  };

  componentDidUpdate(prevProps: Readonly<IComponentProps>) {
    if (
      this.props.filterUserName !== null &&
      this.props.filterUserName !== prevProps.filterUserName
    )
      this.setState({ nameToSearch: this.props.filterUserName || '' });
    this.setRolesWithUsages();
  }
  componentDidMount() {
    this.setState({ nameToSearch: this.props.filterUserName || '' });
    this.setRolesWithUsages();
  }

  setRolesWithUsages() {
    if (this.state.rolesWithUsagesInProject || !this.props.projects) return;
    const projectsObject: IProjectStore[] = Object.values(this.props.projects);
    const rolesWithUsagesInProject = {};
    projectsObject.forEach((item) => {
      const iProjectMemberViews = Object.values<IProjectMemberView>(
        item.projectRoleMembers as any
      );
      iProjectMemberViews.forEach((pm: IProjectMemberView) => {
        if (pm.roleId) rolesWithUsagesInProject![pm.roleId] = pm.projectId;
      });
    });
    this.setState({ rolesWithUsagesInProject });
  }

  handleAddMemberRole = (role: string | IChipItem) => {
    const roleItem = role as IChipItem;
    return this.props.addRoles(
      [
        {
          roleId: roleItem.id as number,
          userId: roleItem.userId,
        },
      ],
      this.props.companyId
    );
  };

  handleDeleteMemberRole = (role: IChipItem) => {
    const { companyId, projects } = this.props;
    let usedInProjects = '';
    Object.values(projects || {}).forEach((project) => {
      if (
        project.companyId === companyId &&
        project.projectRoleMembers![role.userId.toString()].relatedRoleId ===
          role.id
      ) {
        usedInProjects =
          usedInProjects + (usedInProjects.length ? ', ' : '') + project.name;
      }
    });
    let result = false;
    if (usedInProjects.length)
      result = confirm(messages.confirmRemoveUserWithUsedRoles(usedInProjects));
    if (result || !usedInProjects.length) {
      const userRoles: IUserRoleView[] = [];
      userRoles.push({ roleId: role.id as number, userId: role.userId });
      return this.props.deleteRoles(userRoles, this.props.companyId);
    }
    return Promise.resolve();
  };

  handleSetNameToSearch = (nameToSearch: string) => {
    this.setState({ nameToSearch });
  };

  handleDebounceSearch = debounce(this.handleSetNameToSearch, 500);

  getFilteredCompanyMembers = () => {
    const { nameToSearch, rolesToSearch } = this.state;
    let filteredCompanyMembers: ICompanyMember[] = [];
    const { companyMembers } = this.props;

    const filterByName = nameToSearch !== '';
    const filterByRoles = rolesToSearch.length > 0;

    if (filterByName || filterByRoles) {
      filteredCompanyMembers = Object.values(companyMembers).filter(
        (member) => {
          let hasName;
          let hasRole;
          if (filterByName) {
            hasName = Boolean(
              Object.values(this.props.users).find(
                (user: IUser | undefined) => {
                  return (
                    (user!.id === member.userId &&
                      (user!.name.includes(nameToSearch) ||
                        user!.phoneNumber?.includes(nameToSearch))) ||
                    member.employeeId?.includes(nameToSearch)
                  );
                }
              )
            );
          }
          if (filterByRoles) {
            const memberRoles = this.props.getMemberRoles(member.userId);
            hasRole = memberRoles.some(
              (memberRole) =>
                !!rolesToSearch.find(
                  (selectedRole) => memberRole.id === selectedRole.value
                )
            );
          }
          if (filterByName && filterByRoles) {
            return hasRole && hasName;
          } else if (filterByRoles) {
            return hasRole;
          } else if (filterByName) {
            return hasName;
          }
          return false;
        }
      );
    } else {
      filteredCompanyMembers = Object.values(companyMembers);
    }
    return filteredCompanyMembers.sort((a, b) =>
      a.inactive !== b.inactive ? (a.inactive ? 1 : -1) : a.userId - b.userId
    );
  };

  handleSetRolesToSearch = (rolesToSearch: IOption[]) => {
    this.setState({ rolesToSearch });
  };

  MemberItem = (childProps: ListChildComponentProps) => {
    const {
      users,
      addSignature,
      getMemberRoles,
      companyOwnerId,
      deleteSignature,
      updateMember,
    } = this.props;

    const member = childProps.data.filteredCompanyMembers[childProps.index];
    const user: IUser = users[member?.userId];
    const userId = user?.id;
    return (
      <div style={childProps.style}>
        <CompanyMemberView
          key={member.userId}
          user={user}
          member={member}
          onDelete={this.handleDelete}
          onRestore={this.handleRestore}
          defaultRoles={getMemberRoles(userId)}
          isCompanyOwner={companyOwnerId === userId}
          onAddMemberRole={this.handleAddMemberRole}
          onDeleteMemberRole={this.handleDeleteMemberRole}
          onAddMemberSignature={addSignature}
          onRemoveMemberSignature={deleteSignature}
          onUpdateMember={updateMember}
          isActive={this.state.selectedUserId === userId}
          onUserSelect={(userId: number | undefined) =>
            this.setState({ selectedUserId: userId })
          }
          company={this.props.company}
          onUpdatePhone={this.props.changePhone}
          thisUserId={this.props.thisUserId}
        />
      </div>
    );
  };

  handleDeleteMember = (projectId: number) => (memberId: number) => {
    const projectMember = {
      userId: memberId,
      projectId: Number(projectId),
    };
    this.props
      .removeProjectMember(
        projectMember as IProjectMemberView,
        this.props.companyId
      )
      .then(
        () => {
          notify(i18n.t('projectMemberRemoved', { ns: 'companyManagement' }));
        },
        (err: any) => {
          display(err);
        }
      );
  };

  handleUpdateMemberStatus =
    (pm: IProjectMemberView) =>
    (
      _: number,
      type: ProjectMemberConfig,
      value: string | boolean | number | null | undefined
    ) => {
      const member = pm;
      // @ts-expect-error it's ok. false error
      member[type] = value;
      return this.props.updateProjectMember(member, this.props.companyId);
    };

  onExport = async () => {
    return await appDispatch(
      exportMembers({ companyId: this.props.companyId })
    );
  };

  private roleHasError(
    it: ICompanyRole,
    projects: { [key: number]: IProject }
  ): { type: RoleErrorTypes; message: string } | null {
    if (it.isUnique && it.users.length > 1)
      return {
        type: 'extraUser',
        message: i18n.t('exactly1MemberShouldBeAssigned', {
          ns: 'companyManagement',
        }),
      };
    if (it.isUnique && it.users.length < 1)
      return {
        type: 'missingUser',
        message: i18n.t('noOneAssigned', { ns: 'companyManagement' }),
      };
    // if (it.roleType === 'District' && !it.relatedTagId) return true;
    if (it.users.length === 0) {
      if (this.state.rolesWithUsagesInProject?.[it.id])
        return {
          type: 'missingUser',
          message: messages.usedInProjectsButEmpty(
            projects[this.state.rolesWithUsagesInProject[it.id]].name
          ),
        };
    }
    return null;
  }

  handleFilteredOptions = () => {
    const { nameToSearch } = this.state;
    if (!nameToSearch || nameToSearch.trim() === '') {
      return Object.values(this.props.companyRoleOptions);
    }
    const lowerSearch = nameToSearch.trim().toLowerCase();
    return Object.values(this.props.companyRoleOptions).filter(
      (role: IOption) =>
        role.label && role.label.toLowerCase().includes(lowerSearch)
    );
  };

  render() {
    const filteredCompanyMembers = this.getFilteredCompanyMembers();

    const { selectedUserId } = this.state;
    const {
      projectObj,
      projects,
      company,
      companyRoles,
      companyMembersMap,
      users,
    } = this.props;

    const selectedCompanyMemberRoles =
      (selectedUserId &&
        Object.keys(companyMembersMap[selectedUserId].roles)) ||
      [];

    const withErrorRoles: {
      [key: number]: { type: RoleErrorTypes; message: string };
    } = {};
    for (const item in this.props.company.roles) {
      const it = this.props.company.roles[item];
      const hasError = this.roleHasError(it, projectObj);
      if (hasError) withErrorRoles[it.id] = hasError;
    }

    const projectMembers = selectedUserId
      ? Object.values(projects || {}).reduce(
          (current: IProjectMemberView[] | undefined, item) => {
            if (
              item.companyId === company.id &&
              item.projectRoleMembers![selectedUserId.toString()]
            ) {
              current!.push(
                item.projectRoleMembers![selectedUserId.toString()]
              );
            }
            selectedCompanyMemberRoles.forEach((roleId) => {
              if (
                item.companyId === company.id &&
                item.projectRoleMembers![roleId]
              ) {
                current!.push(item.projectRoleMembers![roleId]);
              }
            });

            return current!;
          },
          [] as IProjectMemberView[]
        )
      : undefined;
    const postRoleIds = new Set<number>();
    const districtRoleIds = new Set<number>();
    Object.values(this.props.companyRoles).forEach((r) => {
      if (r!.roleType === 'District') districtRoleIds.add(r!.id);
      if (r!.roleType === 'Post') postRoleIds.add(r!.id);
    });

    const withErrorUsers = Object.values(this.props.companyMembers).filter(
      (member) => {
        if (member!.inactive) return false;

        const keys: number[] = Object.keys(member!.roles).map((it) =>
          Number(it)
        );
        const hasPost = keys.find((i: number) => {
          return postRoleIds.has(i);
        });
        if (!hasPost) return true;
        const hasDistrict = keys.find((i: number) => {
          return districtRoleIds.has(i);
        });
        if (!hasDistrict) return true;
        return false;
      }
    );

    return (
      <AutoSizer style={{ width: '100%' }}>
        {({ height }: { height: number }) => (
          <div>
            <CompanyMembersFilters
              membersCount={filteredCompanyMembers.length}
              rolesToSearch={this.state.rolesToSearch}
              setNameToSearch={this.handleDebounceSearch}
              companyRoleOptions={
                this.state.nameToSearch
                  ? this.handleFilteredOptions()
                  : this.props.companyRoleOptions!
              }
              onSetRolesToSearch={this.handleSetRolesToSearch}
              withErrorUsersCount={withErrorUsers.length}
              withErrorRolesCount={Object.keys(withErrorRoles).length}
              viewState={this.state.viewState}
              setViewState={(viewState) => this.setState({ viewState })}
              onExport={this.onExport}
              initName={this.props.filterUserName}
            />
            <div style={{ display: 'flex' }}>
              {this.state.viewState !== 'errorRoles' ? (
                <div
                  style={{
                    width: '100%',
                  }}
                >
                  <CompanyMembersList
                    height={height}
                    memberItems={this.MemberItem}
                    filteredCompanyMembers={
                      this.state.viewState === 'errorUsers'
                        ? withErrorUsers
                        : filteredCompanyMembers
                    }
                  />
                </div>
              ) : (
                <div>
                  <TranslatedTable className="highlightedTable">
                    <tr>
                      <th>
                        {
                          i18n.t('roleWithError', {
                            ns: 'companyManagement',
                          }) as string
                        }
                      </th>
                      <th>
                        {i18n.t('error', { ns: 'companyManagement' }) as string}
                      </th>{' '}
                      <th>
                        {
                          i18n.t('actions', {
                            ns: 'companyManagement',
                          }) as string
                        }
                      </th>
                    </tr>
                    {Object.entries(withErrorRoles).map(
                      ([roleId, errorType]) => {
                        return (
                          <tr key={roleId}>
                            <td>{company.roles[roleId].displayName}</td>
                            <td>{errorType.message}</td>
                            <td>
                              {errorType.type === 'extraUser' && (
                                <Button
                                  onClick={() => {
                                    this.setState({
                                      viewState: 'default',
                                      rolesToSearch: [
                                        {
                                          value: Number(roleId),
                                          label: company.roles[roleId].name,
                                        },
                                      ],
                                    });
                                  }}
                                >
                                  {
                                    i18n.t('showThem', {
                                      ns: 'companyManagement',
                                    }) as string
                                  }
                                </Button>
                              )}
                              {errorType.type === 'missingUser' && (
                                <p>
                                  {
                                    i18n.t('findUserAndAddRole', {
                                      ns: 'companyManagement',
                                    }) as string
                                  }
                                </p>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </TranslatedTable>
                </div>
              )}
              {selectedUserId && (
                <>
                  <CompanyMemberModal
                    companyMember={companyMembersMap[selectedUserId]}
                    companyObject={this.props.company}
                    isSupport={this.props.isSupport}
                    user={users[selectedUserId]}
                    onClose={() => {
                      this.setState({ selectedUserId: undefined });
                    }}
                    projectMembers={projectMembers}
                    companyRoles={companyRoles}
                    companyMembersMap={companyMembersMap}
                    users={users}
                    projects={projects}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </AutoSizer>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = {
  addRoles: (userRoles: IUserRoleView[], companyId: number) =>
    appDispatch(addUserRoles({ userRoles, companyId })),
  deleteRoles: (userRoles: IUserRoleView[], companyId: number) =>
    appDispatch(deleteUserRoles({ userRoles, companyId })),
  addSignature: (companyId: number, userId: number, file: any) =>
    appDispatch(addMemberSignature({ companyId, userId, file })),
  deleteSignature: (companyId: number, userId: number) =>
    appDispatch(removeMemberSignature({ companyId, userId })),
  updateMember: (member: ICompanyMember) =>
    appDispatch(updateCompanyMember({ companyMember: member })),
  addMember: (member: ICompanyMember) =>
    appDispatch(addMemberToCompany({ companyMember: member })),
  removeProjectMember: (projectMember: IProjectMemberView, companyId: number) =>
    appDispatch(removeProjectMember({ projectMember, companyId })),
  updateProjectMember: (projectMember: IProjectMemberView, companyId: number) =>
    appDispatch(updateProjectMember({ projectMember, companyId })),
  exportCompanyMembers: (companyId: number) =>
    appDispatch(exportMembers({ companyId })),
  changePhone: (
    userId: number,
    currentPhone: string,
    newPhone: string,
    newName?: string
  ) =>
    appDispatch(
      changeUserPhone({
        username: '',
        userId: userId.toString(),
        currentPhone,
        newPhone,
        swapIfExists: true,
        newName,
      })
    ),
};

const mapStateToProps: MapStateToProps<void, IOwnProps, RootState> = (
  _,
  props
) => {
  return { projectObj: props.projects };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanyMembersListWrapper);
